import {useMemo} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {QCutoffPage} from '@/pages/components/audit-program/q-section/constants';
import {Q1_2_PAGE} from '@/pages/components/audit-program/q-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface QCutoffViewProps {
    header: AuditProgramSectionVO;
    page: QCutoffPage;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.35};
const bodySx = {...cellBorder, py: 0.35, px: 0.3, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

const ROW_FIELDS = [
    'date', 'description', 'voucher', 'invoice', 'invoiceDate',
    'cheque', 'details', 'amount', 'wd1', 'wd2', 'wd3',
] as const;

/** Q1.2 — Sales cut-off test working paper. */
export default function QCutoffView({
    header,
    page,
    refCode = 'Q1.2',
    values,
    onChange,
}: QCutoffViewProps) {
    const meta = {
        title: page.title || Q1_2_PAGE.title,
        blankRows: page.blankRows || Q1_2_PAGE.blankRows,
        workdoneItems: page.workdoneItems?.length ? page.workdoneItems : Q1_2_PAGE.workdoneItems,
        objectiveLabel: page.objectiveLabel || Q1_2_PAGE.objectiveLabel,
        objectivePlaceholder: page.objectivePlaceholder || Q1_2_PAGE.objectivePlaceholder,
        conclusionLabel: page.conclusionLabel || Q1_2_PAGE.conclusionLabel,
        conclusionPlaceholder: page.conclusionPlaceholder || Q1_2_PAGE.conclusionPlaceholder,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankRows)}, (_, i) => `r${i + 1}`),
        [meta.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 1100, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} style={{width: 32}}/>
                            <TableCell sx={headSx}>Date</TableCell>
                            <TableCell sx={headSx}>Description</TableCell>
                            <TableCell sx={headSx}>Voucher No.</TableCell>
                            <TableCell sx={headSx}>Invoice</TableCell>
                            <TableCell sx={headSx}>Invoice date</TableCell>
                            <TableCell sx={headSx}>Cheque No.</TableCell>
                            <TableCell sx={headSx}>Details</TableCell>
                            <TableCell sx={headSx} align="right">Amount</TableCell>
                            <TableCell sx={headSx} align="center" colSpan={3}>Workdone</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={9}/>
                            <TableCell sx={headSx} align="center">(1)</TableCell>
                            <TableCell sx={headSx} align="center">(2)</TableCell>
                            <TableCell sx={headSx} align="center">(3)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" color="text.secondary">{idx + 1}</Typography>
                                </TableCell>
                                {ROW_FIELDS.map((field) => (
                                    <TableCell key={field} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align={
                                                field === 'amount' || field.startsWith('wd')
                                                    ? 'center'
                                                    : 'left'
                                            }
                                            sx={fieldSx}
                                            value={values[`${id}.${field}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${field}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                Workdone
            </Typography>
            <Stack spacing={0.25} sx={{mb: 2}}>
                {meta.workdoneItems.map((item, idx) => (
                    <Typography key={idx} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        {idx + 1}. {item}
                    </Typography>
                ))}
            </Stack>

            <Stack spacing={1.5} sx={{maxWidth: 820}}>
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                        {meta.objectiveLabel}
                    </Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        multiline
                        minRows={2}
                        placeholder={meta.objectivePlaceholder}
                        value={values.objective ?? ''}
                        onChange={(e) => onChange('objective', e.target.value)}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                        {meta.conclusionLabel}
                    </Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        multiline
                        minRows={2}
                        placeholder={meta.conclusionPlaceholder}
                        value={values.conclusion ?? ''}
                        onChange={(e) => onChange('conclusion', e.target.value)}
                    />
                </Box>
            </Stack>
        </Box>
    );
}
